import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Typography,
  Descriptions,
  Badge,
  DatePicker,
  Tooltip,
  Divider,
  Empty
} from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  BankOutlined,
  MessageOutlined,
  CalendarOutlined,
  FilterOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import courseService from '../../services/course.service';
// Removed moment dependency - using native JavaScript Date APIs

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Utility functions for date formatting (replacing moment)
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).replace(',', ' à');
};

const isDateInRange = (date, startDate, endDate) => {
  if (!date || !startDate || !endDate) return true;
  const d = new Date(date);
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Set time to start of day for comparison
  d.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  
  return d >= start && d <= end;
};

const SalesRequestManager = () => {
  const [salesRequests, setSalesRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    requestType: 'all',
    dateRange: null
  });

  // Options pour les types de demandes
  const requestTypeOptions = [
    { value: 'info', label: '💡 Demande d\'informations', color: '#1890ff' },
    { value: 'devis', label: '💰 Demande de devis', color: '#52c41a' },
    { value: 'demo', label: '🎯 Demander une démo', color: '#fa8c16' },
    { value: 'autre', label: '❓ Autre', color: '#722ed1' }
  ];

  // Options pour les statuts
  const statusOptions = [
    { value: 'PENDING', label: 'En attente', color: 'orange' },
    { value: 'CONTACTED', label: 'Contacté', color: 'blue' },
    { value: 'CLOSED', label: 'Fermé', color: 'green' }
  ];

  useEffect(() => {
    fetchSalesRequests();
  }, []);

  const fetchSalesRequests = async () => {
    setLoading(true);
    try {
      const response = await courseService.getSalesRequests(); // Vous devrez ajouter cette méthode
      setSalesRequests(response);
    } catch (error) {
      message.error('Erreur lors du chargement des demandes commerciales');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await courseService.updateSalesRequestStatus(id, newStatus); // Vous devrez ajouter cette méthode
      message.success('Statut mis à jour avec succès');
      fetchSalesRequests();
    } catch (error) {
      message.error('Erreur lors de la mise à jour du statut');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await courseService.deleteSalesRequest(id); // Vous devrez ajouter cette méthode
      message.success('Demande supprimée avec succès');
      fetchSalesRequests();
    } catch (error) {
      message.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailModalVisible(true);
  };

  const handleEdit = (request) => {
    setEditingRequest(request);
    form.setFieldsValue({
      status: request.status,
      notes: request.notes || ''
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await courseService.updateSalesRequest(editingRequest.id, values); // Vous devrez ajouter cette méthode
      message.success('Demande mise à jour avec succès');
      setModalVisible(false);
      fetchSalesRequests();
    } catch (error) {
      message.error('Erreur lors de la mise à jour');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const getRequestTypeInfo = (type) => {
    return requestTypeOptions.find(option => option.value === type) || 
           { label: type, color: '#666' };
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(option => option.value === status) || 
           { label: status, color: 'default' };
  };

  const filteredData = salesRequests.filter(request => {
    if (filters.status !== 'all' && request.status !== filters.status) return false;
    if (filters.requestType !== 'all' && request.requestType !== filters.requestType) return false;
    if (filters.dateRange && filters.dateRange.length === 2) {
      const requestDate = request.createdAt;
      if (!isDateInRange(requestDate, filters.dateRange[0], filters.dateRange[1])) {
        return false;
      }
    }
    return true;
  });

  const columns = [
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <Text strong>{record.firstName} {record.lastName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <MailOutlined style={{ marginRight: 4 }} />
            {record.email}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <BankOutlined style={{ marginRight: 4 }} />
            {record.company}
          </Text>
        </div>
      ),
    },
    {
      title: 'Type de demande',
      dataIndex: 'requestType',
      key: 'requestType',
      render: (type) => {
        const typeInfo = getRequestTypeInfo(type);
        return (
          <Tag color={typeInfo.color}>
            {typeInfo.label}
          </Tag>
        );
      },
      filters: requestTypeOptions.map(option => ({
        text: option.label,
        value: option.value
      })),
      onFilter: (value, record) => record.requestType === value,
    },
    {
      title: 'Plan associé',
      key: 'plan',
      render: (_, record) => (
        record.planTitle ? (
          <div>
            <Text>{record.planTitle}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ID: {record.planId}
            </Text>
          </div>
        ) : (
          <Text type="secondary">Aucun plan</Text>
        )
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const statusInfo = getStatusInfo(status);
        return (
          <Select
            value={status}
            style={{ width: 120 }}
            size="small"
            onChange={(newStatus) => handleStatusUpdate(record.id, newStatus)}
          >
            {statusOptions.map(option => (
              <Option key={option.value} value={option.value}>
                <Badge color={option.color} text={option.label} />
              </Option>
            ))}
          </Select>
        );
      },
      filters: statusOptions.map(option => ({
        text: option.label,
        value: option.value
      })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <div>
          <Text>{formatDate(date)}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {formatTime(date)}
          </Text>
        </div>
      ),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Voir les détails">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Supprimer cette demande ?"
            description="Cette action est irréversible."
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
            okType="danger"
          >
            <Tooltip title="Supprimer">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Title level={3}>
          <MailOutlined style={{ color: '#1890ff', marginRight: 8 }} />
          Gestion des Demandes Commerciales
        </Title>
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchSalesRequests}
            loading={loading}
          >
            Actualiser
          </Button>
        </Space>
      </div>

      {/* Filtres rapides */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong>Filtres rapides :</Text>
          </Col>
          <Col>
            <Select
              style={{ width: 150 }}
              placeholder="Statut"
              value={filters.status}
              onChange={(value) => setFilters({...filters, status: value})}
            >
              <Option value="all">Tous les statuts</Option>
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              style={{ width: 180 }}
              placeholder="Type de demande"
              value={filters.requestType}
              onChange={(value) => setFilters({...filters, requestType: value})}
            >
              <Option value="all">Tous les types</Option>
              {requestTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <DatePicker.RangePicker
              style={{ width: 240 }}
              placeholder={['Date début', 'Date fin']}
              onChange={(dates) => setFilters({...filters, dateRange: dates})}
            />
          </Col>
          <Col>
            <Button 
              onClick={() => setFilters({ status: 'all', requestType: 'all', dateRange: null })}
            >
              Réinitialiser
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Statistiques rapides */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                {salesRequests.length}
              </Title>
              <Text type="secondary">Total demandes</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <Title level={4} style={{ margin: 0, color: '#fa8c16' }}>
                {salesRequests.filter(r => r.status === 'PENDING').length}
              </Title>
              <Text type="secondary">En attente</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
                {salesRequests.filter(r => r.status === 'CONTACTED').length}
              </Title>
              <Text type="secondary">Contactées</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <Title level={4} style={{ margin: 0, color: '#722ed1' }}>
                {salesRequests.filter(r => r.status === 'CLOSED').length}
              </Title>
              <Text type="secondary">Fermées</Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} demandes`
        }}
        locale={{
          emptyText: <Empty description="Aucune demande commerciale trouvée" />
        }}
      />

      {/* Modal des détails */}
      <Modal
        title={
          <span>
            <EyeOutlined style={{ marginRight: 8 }} />
            Détails de la demande commerciale
          </span>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Fermer
          </Button>
        ]}
        width={700}
      >
        {selectedRequest && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Nom complet" span={2}>
                <Text strong>{selectedRequest.firstName} {selectedRequest.lastName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <a href={`mailto:${selectedRequest.email}`}>
                  <MailOutlined style={{ marginRight: 4 }} />
                  {selectedRequest.email}
                </a>
              </Descriptions.Item>
              <Descriptions.Item label="Entreprise">
                <BankOutlined style={{ marginRight: 4 }} />
                {selectedRequest.company}
              </Descriptions.Item>
              <Descriptions.Item label="Type de demande">
                {(() => {
                  const typeInfo = getRequestTypeInfo(selectedRequest.requestType);
                  return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                {(() => {
                  const statusInfo = getStatusInfo(selectedRequest.status);
                  return <Badge color={statusInfo.color} text={statusInfo.label} />;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Date de création" span={2}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                {formatDateTime(selectedRequest.createdAt)}
              </Descriptions.Item>
              {selectedRequest.planTitle && (
                <>
                  <Descriptions.Item label="Plan associé">
                    {selectedRequest.planTitle}
                  </Descriptions.Item>
                  <Descriptions.Item label="ID du plan">
                    {selectedRequest.planId}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>

            {selectedRequest.message && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>
                  <MessageOutlined style={{ marginRight: 8 }} />
                  Message du client :
                </Title>
                <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {selectedRequest.message}
                  </Paragraph>
                </Card>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal d'édition */}
      <Modal
        title={
          <span>
            <EditOutlined style={{ marginRight: 8 }} />
            Modifier la demande
          </span>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSubmitting(false);
        }}
        footer={null}
        width={500}
        maskClosable={!submitting}
        closable={!submitting}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="status"
            label="Statut"
            rules={[{ required: true, message: 'Le statut est obligatoire' }]}
          >
            <Select placeholder="Sélectionner un statut">
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  <Badge color={option.color} text={option.label} />
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes internes (optionnel)"
          >
            <TextArea
              rows={4}
              placeholder="Ajoutez des notes sur cette demande..."
            />
          </Form.Item>

          <div style={{ textAlign: 'right', marginTop: '24px' }}>
            <Space>
              <Button 
                onClick={() => setModalVisible(false)}
                disabled={submitting}
              >
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
              >
                Mettre à jour
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default SalesRequestManager;